import React, { useState, useCallback, useEffect } from 'react';
import Prognose from './Prognose';
import {
  Box, Tabs, Tab, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, IconButton, Tooltip, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, InputAdornment,
  Autocomplete, Divider, Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Store as SupplierIcon,
  ShoppingCart as OrderIcon,
  Inventory as StockValueIcon,
  TrendingUp as ForecastIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as ReceiveIcon,
  FileDownload as DownloadIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  ArrowBack as BackIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

const APP_API_KEY = process.env.REACT_APP_API_KEY || 'e4ccbc0bd680211f4e4553ea3ab07d8a55707e79cde61faa10c51c55c6da35a9';
const API = '/api/purchase';
const INV_API = '/api/inventory';

function useApi(endpoint, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(endpoint, { headers: { 'x-api-key': APP_API_KEY } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [endpoint, ...deps]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

const STATUS_LABELS = {
  draft: { label: 'Concept', color: 'default' },
  ordered: { label: 'Besteld', color: 'warning' },
  partial: { label: 'Deels ontvangen', color: 'info' },
  received: { label: 'Ontvangen', color: 'success' },
  cancelled: { label: 'Geannuleerd', color: 'error' },
};

const WAREHOUSES = ['Dion', 'Vincent', 'Olijslager', 'InnoStock'];

// ─── Tab 0: Leveranciers ──────────────────────────────────────────────────────

// ─── SKU Koppeling Tab ──────────────────────────────────────────────────────
function SkuLinkTab() {
  const { data: suppliers, loading: suppLoading } = useApi(`${API}/suppliers`);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${INV_API}/products`, { headers: { 'x-api-key': APP_API_KEY } });
    const data = await res.json();
    setProducts(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${INV_API}/products/sync`, {
        method: 'POST',
        headers: { 'x-api-key': APP_API_KEY }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSyncResult({ ok: true, msg: `${json.synced} gesynced, ${json.skipped} overgeslagen (${json.total} in Picqer)` });
      await loadProducts();
    } catch (e) {
      setSyncResult({ ok: false, msg: e.message });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 6000);
    }
  };

  const handleSupplierChange = async (product, supplierId) => {
    setSaving(product.id);
    await fetch(`${INV_API}/products/${product.id}/minlevel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify({ supplier_id: supplierId || null })
    });
    setProducts(prev => prev.map(p =>
      p.id === product.id ? { ...p, supplier_id: supplierId || null } : p
    ));
    setSaving(null);
  };

  const filtered = products.filter(p => {
    if (filterSupplier !== 'all') {
      if (filterSupplier === '0') {
        // "Niet gekoppeld" — toon alleen producten zonder leverancier
        if (p.supplier_id) return false;
      } else if (p.supplier_id !== parseInt(filterSupplier)) {
        return false;
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return p.sku?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
    }
    return true;
  });

  const supplierMap = {};
  (suppliers || []).forEach(s => { supplierMap[s.id] = s; });

  if (loading || suppLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const linked = products.filter(p => p.supplier_id).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight="bold">SKU — Leverancier koppeling</Typography>
        <Chip label={`${linked}/${products.length} gekoppeld`}
          color={linked === products.length ? 'success' : 'warning'} size="small" />
        <TextField size="small" placeholder="Zoek SKU of product..."
          value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 220 }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)}>
            <MenuItem value="all">Alle leveranciers</MenuItem>
            <MenuItem value="0">Niet gekoppeld</MenuItem>
            {(suppliers || []).map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Haal nieuwe producten op uit Picqer/InnoStock">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              Sync Picqer
            </Button>
          </span>
        </Tooltip>
        {syncResult && (
          <Alert severity={syncResult.ok ? 'success' : 'error'} sx={{ py: 0, px: 1 }}>
            {syncResult.msg}
          </Alert>
        )}
      </Box>

      <TableContainer component={Paper} elevation={0}
        sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ '& th': { backgroundColor: '#f5f5f5', fontWeight: 'bold' } }}>
            <TableRow>
              <TableCell sx={{ minWidth: 100 }}>SKU</TableCell>
              <TableCell sx={{ minWidth: 260 }}>Product</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Leverancier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(p => (
              <TableRow key={p.id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5',
                    px: 0.8, py: 0.3, borderRadius: 1 }}>{p.sku}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{p.name}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={p.supplier_id || ''}
                        onChange={e => handleSupplierChange(p, e.target.value || null)}
                        displayEmpty
                        sx={{ fontSize: 13 }}
                      >
                        <MenuItem value=""><em style={{ color: '#aaa' }}>— Geen leverancier —</em></MenuItem>
                        {(suppliers || []).map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {saving === p.id && <CircularProgress size={14} />}
                    {p.supplier_id && saving !== p.id && (
                      <CheckIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Geen producten gevonden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function SuppliersTab() {
  const { data: suppliers, loading, refetch } = useApi(`${API}/suppliers`);
  const [dialog, setDialog] = useState(null); // null | 'new' | supplier object
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statsDialog, setStatsDialog] = useState(null);
  const [stats, setStats] = useState(null);

  const openNew = () => { setForm({}); setDialog('new'); };
  const openEdit = (s) => { setForm({ ...s }); setDialog(s); };

  const handleSave = async () => {
    setSaving(true);
    const isEdit = dialog !== 'new';
    const url = isEdit ? `${API}/suppliers/${dialog.id}` : `${API}/suppliers`;
    await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify(form)
    });
    setSaving(false);
    setDialog(null);
    refetch();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Leverancier verwijderen?')) return;
    await fetch(`${API}/suppliers/${id}`, { method: 'DELETE', headers: { 'x-api-key': APP_API_KEY } });
    refetch();
  };

  const openStats = async (s) => {
    setStatsDialog(s);
    setStats(null);
    const res = await fetch(`${API}/suppliers/${s.id}/stats`, { headers: { 'x-api-key': APP_API_KEY } });
    setStats(await res.json());
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Leveranciers</Typography>
        <Button startIcon={<AddIcon />} variant="contained" size="small"
          sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#e6b800' } }}
          onClick={openNew}>Nieuwe leverancier</Button>
      </Box>

      {(!suppliers || suppliers.length === 0) ? (
        <Alert severity="info">Nog geen leveranciers aangemaakt.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Naam</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contactpersoon</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Telefoon</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Notitie</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Button size="small" sx={{ p: 0, fontWeight: 'bold', textTransform: 'none' }}
                      onClick={() => openStats(s)}>{s.name}</Button>
                  </TableCell>
                  <TableCell><Typography variant="body2">{s.contact_person || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{s.email || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{s.phone || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{s.notes || '—'}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Bewerken">
                      <IconButton size="small" onClick={() => openEdit(s)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Verwijderen">
                      <IconButton size="small" onClick={() => handleDelete(s.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Formulier dialog */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog === 'new' ? 'Nieuwe leverancier' : 'Leverancier bewerken'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Naam *" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} fullWidth size="small" />
          <TextField label="Contactpersoon" value={form.contact_person || ''} onChange={e => setForm(p => ({ ...p, contact_person: e.target.value }))} fullWidth size="small" />
          <TextField label="E-mailadres" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} fullWidth size="small" type="email" />
          <TextField label="Telefoon" value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} fullWidth size="small" />
          <TextField label="Adres" value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} fullWidth size="small" multiline rows={2} />
          <TextField label="Notitie" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} fullWidth size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Annuleren</Button>
          <Button onClick={handleSave} disabled={!form.name || saving} variant="contained"
            sx={{ backgroundColor: '#ffcc00', color: '#000' }}>
            {saving ? <CircularProgress size={18} /> : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={!!statsDialog} onClose={() => setStatsDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>{statsDialog?.name} — Overzicht</DialogTitle>
        <DialogContent>
          {!stats ? <CircularProgress /> : (
            <Box>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1, textAlign: 'center', border: '1px solid #e0e0e0' }} elevation={0}>
                  <Typography variant="h5" fontWeight="bold">{stats.orderedCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Orders onderweg</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, textAlign: 'center', border: '1px solid #e0e0e0' }} elevation={0}>
                  <Typography variant="h5" fontWeight="bold">€ {stats.outstanding?.toFixed(2)}</Typography>
                  <Typography variant="caption" color="text.secondary">Waarde onderweg</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, textAlign: 'center', border: '1px solid #e0e0e0' }} elevation={0}>
                  <Typography variant="h5" fontWeight="bold">€ {stats.totalOrdered?.toFixed(2)}</Typography>
                  <Typography variant="caption" color="text.secondary">Totaal ingekocht</Typography>
                </Paper>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>PO nummer</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Verwacht</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Waarde</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.orders.map(o => (
                      <TableRow key={o.id} hover>
                        <TableCell><Typography variant="body2" fontWeight="bold">{o.po_number}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{new Date(o.created_at).toLocaleDateString('nl-NL')}</Typography></TableCell>
                        <TableCell><Chip {...STATUS_LABELS[o.status]} size="small" /></TableCell>
                        <TableCell><Typography variant="caption">{o.expected_date ? new Date(o.expected_date).toLocaleDateString('nl-NL') : '—'}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2">€ {parseFloat(o.total_value || 0).toFixed(2)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialog(null)}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Tab 1: Inkooporders ──────────────────────────────────────────────────────
function OrdersTab({ products, refetchProducts }) {
  const { data: orders, loading, refetch } = useApi(`${API}/orders`);
  const { data: suppliers } = useApi(`${API}/suppliers`);
  const [view, setView] = useState('list'); // 'list' | 'new' | 'edit' | 'detail' | 'receive'
  const [selectedPo, setSelectedPo] = useState(null);
  const [poDetail, setPoDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sendDialog, setSendDialog] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadDetail = async (id) => {
    const res = await fetch(`${API}/orders/${id}`, { headers: { 'x-api-key': APP_API_KEY } });
    const data = await res.json();
    setPoDetail(data);
  };

  const openDetail = async (po) => {
    setSelectedPo(po);
    await loadDetail(po.id);
    setView('detail');
  };

  const openReceive = async (po) => {
    setSelectedPo(po);
    await loadDetail(po.id);
    setView('receive');
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/orders/${sendDialog.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({ email: sendEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSendResult({ ok: true, msg: `Verstuurd naar ${data.sent_to}` });
    } catch (e) {
      setSendResult({ ok: false, msg: e.message });
    }
    setSending(false);
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const filtered = (orders || []).filter(o =>
    filterStatus === 'all' || o.status === filterStatus
  );

  if (view === 'new') {
    return <NewOrderForm products={products} suppliers={suppliers || []}
      refetchProducts={refetchProducts}
      onSaved={() => { refetch(); setView('list'); }} onCancel={() => setView('list')} />;
  }

  if (view === 'edit' && poDetail) {
    return <NewOrderForm products={products} suppliers={suppliers || []}
      refetchProducts={refetchProducts}
      existingPo={poDetail}
      onSaved={() => { loadDetail(poDetail.id); setView('detail'); refetch(); }}
      onCancel={() => setView('detail')} />;
  }

  if (view === 'detail' && poDetail) {
    return <OrderDetail po={poDetail} onBack={() => { setView('list'); refetch(); }}
      onEdit={() => setView('edit')}
      onReceive={() => setView('receive')}
      onSendEmail={(po) => { setSendDialog(po); setSendEmail(po.suppliers?.email || ''); setSendResult(null); }}
      onRefresh={() => loadDetail(poDetail.id)} />;
  }

  if (view === 'receive' && poDetail) {
    return <ReceiveForm po={poDetail}
      onBack={() => { setView('detail'); loadDetail(poDetail.id); }}
      onSaved={() => { loadDetail(poDetail.id); setView('detail'); refetch(); }} />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">Inkooporders</Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="all">Alle statussen</MenuItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={refetch} size="small">Vernieuwen</Button>
          <Button startIcon={<AddIcon />} variant="contained" size="small"
            sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#e6b800' } }}
            onClick={() => setView('new')}>Nieuwe order</Button>
        </Box>
      </Box>

      {filtered.length === 0 ? (
        <Alert severity="info">Geen inkooporders gevonden.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>PO nummer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Leverancier</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Magazijn</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Aangemaakt</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Verwacht</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Waarde</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(po => (
                <TableRow key={po.id} hover>
                  <TableCell>
                    <Button size="small" sx={{ p: 0, fontWeight: 'bold', textTransform: 'none' }}
                      onClick={() => openDetail(po)}>{po.po_number}</Button>
                  </TableCell>
                  <TableCell><Typography variant="body2">{po.suppliers?.name || '—'}</Typography></TableCell>
                  <TableCell><Chip {...STATUS_LABELS[po.status]} size="small" /></TableCell>
                  <TableCell><Typography variant="body2">{po.warehouse}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{new Date(po.created_at).toLocaleDateString('nl-NL')}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{po.expected_date ? new Date(po.expected_date).toLocaleDateString('nl-NL') : '—'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">€ {parseFloat(po.total_value || 0).toFixed(2)}</Typography></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Bekijken">
                      <IconButton size="small" onClick={() => openDetail(po)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                    {['ordered','partial'].includes(po.status) && (
                      <Tooltip title="Ontvangst registreren">
                        <IconButton size="small" onClick={() => openReceive(po)} color="success"><ReceiveIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Verstuur per e-mail">
                      <IconButton size="small" onClick={() => { setSendDialog(po); setSendEmail(po.suppliers?.email || ''); setSendResult(null); }}>
                        <EmailIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Email dialog */}
      <Dialog open={!!sendDialog} onClose={() => setSendDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Verstuur inkooporder</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Order <strong>{sendDialog?.po_number}</strong> wordt als e-mail verstuurd naar:
          </Typography>
          <TextField label="E-mailadres leverancier" value={sendEmail}
            onChange={e => setSendEmail(e.target.value)} fullWidth size="small" type="email" />
          {sendResult && (
            <Alert severity={sendResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
              {sendResult.msg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog(null)}>Sluiten</Button>
          <Button onClick={handleSend} disabled={!sendEmail || sending} variant="contained"
            startIcon={sending ? <CircularProgress size={16} /> : <EmailIcon />}
            sx={{ backgroundColor: '#ffcc00', color: '#000' }}>Versturen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Nieuwe order formulier ───────────────────────────────────────────────────
function NewOrderForm({ products, suppliers, onSaved, onCancel, existingPo, refetchProducts }) {
  const isEdit = !!existingPo;
  const [form, setForm] = useState({
    supplier_id: existingPo?.supplier_id || '',
    expected_date: existingPo?.expected_date || '',
    warehouse: existingPo?.warehouse || 'Dion',
    status: existingPo?.status || 'draft',
    notes: existingPo?.notes || '',
  });
  const [items, setItems] = useState(existingPo?.items || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);

  // Refresh products list bij openen — pakt nieuw gekoppelde leveranciers/SKU's mee
  // refetchProducts is een useCallback uit useApi → stabiele identity, dus effectief alleen on mount
  useEffect(() => { refetchProducts?.(); }, [refetchProducts]);

  // Producten gefilterd op gekozen leverancier (toggle om alles te tonen)
  // Coerce naar Number — form.supplier_id komt soms als string uit <Select>,
  // p.supplier_id is number uit Postgres → strict === zou alle producten weren
  const productsForPicker = (products || []).filter(p => {
    if (showAllProducts) return true;
    if (!form.supplier_id) return true;
    return Number(p.supplier_id) === Number(form.supplier_id);
  });

  const addItem = () => setItems(prev => [...prev, { sku: '', name: '', quantity_ordered: 1, unit_price: 0, product_id: null }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => setItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item));

  const selectProduct = (i, prod) => {
    if (!prod) return;
    setItems(prev => prev.map((item, idx) => {
      if (idx !== i) return item;
      // Behoud een handmatig ingevulde prijs (niet overschrijven met avg)
      const userTypedPrice = parseFloat(item.unit_price) > 0;
      return {
        ...item, sku: prod.sku, name: prod.name,
        product_id: prod.id,
        unit_price: userTypedPrice ? item.unit_price : parseFloat(prod.avg_purchase_price || 0)
      };
    }));
  };

  const total = items.reduce((s, i) => s + (parseFloat(i.quantity_ordered) || 0) * (parseFloat(i.unit_price) || 0), 0);

  const handleSave = async (status) => {
    if (!form.supplier_id) { setError('Selecteer een leverancier'); return; }
    if (items.length === 0) { setError('Voeg minimaal één product toe'); return; }
    if (items.some(i => !i.sku)) { setError('Vul alle SKU\'s in'); return; }

    setSaving(true);
    setError(null);
    try {
      const url = isEdit ? `${API}/orders/${existingPo.id}` : `${API}/orders`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({ ...form, status, items })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onCancel}><BackIcon /></IconButton>
        <Typography variant="h6" fontWeight="bold">{isEdit ? 'Order bewerken' : 'Nieuwe inkooporder'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Leverancier *</InputLabel>
            <Select value={form.supplier_id} label="Leverancier *"
              onChange={e => setForm(p => ({ ...p, supplier_id: e.target.value }))}>
              {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Magazijn</InputLabel>
            <Select value={form.warehouse} label="Magazijn"
              onChange={e => setForm(p => ({ ...p, warehouse: e.target.value }))}>
              {WAREHOUSES.map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Verwachte leverdatum" type="date" value={form.expected_date || ''}
            onChange={e => setForm(p => ({ ...p, expected_date: e.target.value }))}
            fullWidth size="small" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Notitie" value={form.notes || ''}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            fullWidth size="small" />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight="bold">Producten</Typography>
          {form.supplier_id && (
            <Chip
              size="small"
              label={showAllProducts ? 'Toont alle producten' : `Alleen van leverancier (${productsForPicker.length})`}
              color={showAllProducts ? 'default' : 'primary'}
              variant="outlined"
              onClick={() => setShowAllProducts(v => !v)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Product toevoegen</Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 90 }}>Aantal</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: 110 }}>Inkoopprijs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: 100 }}>Totaal</TableCell>
              <TableCell sx={{ width: 40 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={productsForPicker}
                    getOptionLabel={p => `${p.sku} — ${p.name}`}
                    value={(products || []).find(p => p.sku === item.sku) || null}
                    onChange={(_, val) => selectProduct(i, val)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder={
                        form.supplier_id && !showAllProducts
                          ? 'Zoek product van deze leverancier...'
                          : 'Zoek product...'
                      } size="small" />
                    )}
                    filterOptions={(opts, { inputValue }) =>
                      opts.filter(p =>
                        p.sku.toLowerCase().includes(inputValue.toLowerCase()) ||
                        p.name.toLowerCase().includes(inputValue.toLowerCase())
                      ).slice(0, 20)
                    }
                    noOptionsText={
                      form.supplier_id && !showAllProducts
                        ? 'Geen producten van deze leverancier — klik "Toon alle" rechtsboven'
                        : 'Geen producten gevonden'
                    }
                  />
                  {item.sku && <Typography variant="caption" color="text.secondary">{item.sku}</Typography>}
                </TableCell>
                <TableCell align="center">
                  <TextField type="number" size="small" value={item.quantity_ordered}
                    onChange={e => updateItem(i, 'quantity_ordered', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 1, style: { textAlign: 'center', width: 60 } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" value={item.unit_price}
                    onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)}
                    InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    inputProps={{ min: 0, step: 0.01, style: { width: 70 } }} />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    € {((parseFloat(item.quantity_ordered) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => removeItem(i)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length > 0 && (
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Totaal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>€ {total.toFixed(2)}</TableCell>
                <TableCell />
              </TableRow>
            )}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Nog geen producten toegevoegd
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={saving}>Annuleren</Button>
        <Button onClick={() => handleSave('draft')} disabled={saving} variant="outlined">
          {saving ? <CircularProgress size={18} /> : 'Opslaan als concept'}
        </Button>
        <Button onClick={() => handleSave('ordered')} disabled={saving} variant="contained"
          sx={{ backgroundColor: '#ffcc00', color: '#000', '&:hover': { backgroundColor: '#e6b800' } }}>
          {saving ? <CircularProgress size={18} /> : 'Markeer als besteld'}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Order detail ─────────────────────────────────────────────────────────────
function OrderDetail({ po, onBack, onEdit, onReceive, onSendEmail, onRefresh }) {
  const [updating, setUpdating] = useState(false);

  const updateStatus = async (status) => {
    setUpdating(true);
    await fetch(`${API}/orders/${po.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify({ status })
    });
    setUpdating(false);
    onRefresh();
  };

  const st = STATUS_LABELS[po.status];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack}><BackIcon /></IconButton>
        <Typography variant="h6" fontWeight="bold">{po.po_number}</Typography>
        <Chip label={st.label} color={st.color} size="small" />
        <Box sx={{ flex: 1 }} />
        {po.status === 'draft' && (
          <Button size="small" startIcon={<EditIcon />}
            onClick={onEdit} variant="outlined">Bewerken</Button>
        )}
        <Button size="small" startIcon={<DownloadIcon />}
          onClick={() => window.open(`${API}/orders/${po.id}/html?_key=${APP_API_KEY}`, '_blank')}
          variant="outlined">Download PDF</Button>
        <Button size="small" startIcon={<EmailIcon />}
          onClick={() => onSendEmail(po)} variant="outlined">Verstuur naar leverancier</Button>
        {['ordered','partial'].includes(po.status) && (
          <Button size="small" startIcon={<ReceiveIcon />} onClick={onReceive}
            variant="contained" sx={{ backgroundColor: '#ffcc00', color: '#000' }}>
            Ontvangst registreren
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Leverancier</Typography>
            <Typography fontWeight="bold">{po.suppliers?.name || '—'}</Typography>
            {po.suppliers?.email && <Typography variant="body2">{po.suppliers.email}</Typography>}
            {po.suppliers?.phone && <Typography variant="body2">{po.suppliers.phone}</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">Details</Typography>
            <Typography variant="body2">Magazijn: <strong>{po.warehouse}</strong></Typography>
            <Typography variant="body2">Aangemaakt: {new Date(po.created_at).toLocaleDateString('nl-NL')}</Typography>
            <Typography variant="body2">Verwacht: {po.expected_date ? new Date(po.expected_date).toLocaleDateString('nl-NL') : '—'}</Typography>
            {po.notes && <Typography variant="body2" sx={{ mt: 1 }}>Notitie: {po.notes}</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Typography fontWeight="bold" sx={{ mb: 1 }}>Orderregels</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Besteld</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ontvangen</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nog te ontvangen</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prijs/stuk</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Totaal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(po.items || []).map(item => {
              const remaining = item.quantity_ordered - item.quantity_received;
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                  </TableCell>
                  <TableCell align="center">{item.quantity_ordered}</TableCell>
                  <TableCell align="center">
                    <Chip label={item.quantity_received}
                      color={item.quantity_received >= item.quantity_ordered ? 'success' : item.quantity_received > 0 ? 'warning' : 'default'}
                      size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color={remaining > 0 ? 'error' : 'success.main'} fontWeight="bold">
                      {remaining}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">€ {parseFloat(item.unit_price).toFixed(2)}</TableCell>
                  <TableCell align="right">€ {(item.quantity_ordered * item.unit_price).toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>Totaal</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                € {parseFloat(po.total_value || 0).toFixed(2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ontvangstgeschiedenis */}
      {po.receipts && po.receipts.length > 0 && (
        <>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>Ontvangstgeschiedenis</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ontvangen</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prijs/stuk</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Magazijn</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Notitie</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {po.receipts.map(r => (
                  <TableRow key={r.id}>
                    <TableCell><Typography variant="caption">{new Date(r.received_at).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.sku}</Typography></TableCell>
                    <TableCell align="center">{r.quantity_received}</TableCell>
                    <TableCell align="right">€ {parseFloat(r.unit_price).toFixed(2)}</TableCell>
                    <TableCell>{r.warehouse}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{r.notes || '—'}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Status acties */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {po.status === 'draft' && (
          <Button onClick={() => updateStatus('ordered')} disabled={updating} variant="contained"
            sx={{ backgroundColor: '#ffcc00', color: '#000' }}>
            Markeer als besteld
          </Button>
        )}
        {['draft', 'ordered', 'partial'].includes(po.status) && (
          <Button
            onClick={() => {
              const warn = po.status === 'partial'
                ? `Deze inkooporder is al gedeeltelijk ontvangen.\n\nAnnuleren betekent dat de PO de status 'Geannuleerd' krijgt, maar de reeds geboekte voorraad blijft staan (rol je zelf terug indien nodig).\n\nDoorgaan?`
                : po.status === 'ordered'
                  ? `Inkooporder ${po.po_number || ''} is al naar de leverancier gestuurd. Weet je zeker dat je hem wilt annuleren?`
                  : null;
              if (warn && !window.confirm(warn)) return;
              updateStatus('cancelled');
            }}
            disabled={updating}
            color="error"
            variant="outlined"
          >
            Annuleren
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ─── Ontvangst formulier ──────────────────────────────────────────────────────
function ReceiveForm({ po, onBack, onSaved }) {
  const openItems = (po.items || []).filter(i => i.quantity_received < i.quantity_ordered);
  const [receiptLines, setReceiptLines] = useState(
    openItems.map(i => ({
      po_item_id: i.id,
      sku: i.sku,
      name: i.name,
      quantity_ordered: i.quantity_ordered,
      quantity_received_so_far: i.quantity_received,
      quantity_to_receive: i.quantity_ordered - i.quantity_received,
      unit_price: parseFloat(i.unit_price),
      notes: ''
    }))
  );
  const [warehouse, setWarehouse] = useState(po.warehouse);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updateLine = (i, field, value) =>
    setReceiptLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const handleSave = async () => {
    const toReceive = receiptLines.filter(l => l.quantity_to_receive > 0);
    if (toReceive.length === 0) { setError('Voer minimaal één ontvangen hoeveelheid in'); return; }

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/orders/${po.id}/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
        body: JSON.stringify({
          warehouse,
          receipts: toReceive.map(l => ({
            po_item_id: l.po_item_id,
            sku: l.sku,
            quantity_received: l.quantity_to_receive,
            unit_price: l.unit_price,
            notes: l.notes
          }))
        })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack}><BackIcon /></IconButton>
        <Typography variant="h6" fontWeight="bold">Ontvangst registreren — {po.po_number}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Vul per product in hoeveel stuks je nu ontvangt. Je kunt een order in meerdere
        deelleveringen ontvangen — elke ontvangst wordt apart geregistreerd.
      </Alert>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Ontvangst magazijn</InputLabel>
          <Select value={warehouse} label="Ontvangst magazijn"
            onChange={e => setWarehouse(e.target.value)}>
            {WAREHOUSES.map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Besteld</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Al ontvangen</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nu ontvangen</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Inkoopprijs</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Notitie</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receiptLines.map((line, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{line.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{line.sku}</Typography>
                </TableCell>
                <TableCell align="center">{line.quantity_ordered}</TableCell>
                <TableCell align="center">
                  <Chip label={line.quantity_received_so_far} color={line.quantity_received_so_far > 0 ? 'warning' : 'default'} size="small" />
                </TableCell>
                <TableCell align="center">
                  <TextField type="number" size="small" value={line.quantity_to_receive}
                    onChange={e => updateLine(i, 'quantity_to_receive', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0, max: line.quantity_ordered - line.quantity_received_so_far, style: { textAlign: 'center', width: 60 } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" value={line.unit_price}
                    onChange={e => updateLine(i, 'unit_price', parseFloat(e.target.value) || 0)}
                    InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    inputProps={{ min: 0, step: 0.01, style: { width: 70 } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={line.notes}
                    onChange={e => updateLine(i, 'notes', e.target.value)}
                    placeholder="Optioneel..." inputProps={{ style: { width: 120 } }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="success" icon={<InfoIcon />} sx={{ mb: 2 }}>
        Na opslaan wordt de voorraad in <strong>{warehouse}</strong> automatisch bijgewerkt
        en de gewogen gemiddelde inkoopprijs herberekend.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onBack} disabled={saving}>Terug</Button>
        <Button onClick={handleSave} disabled={saving} variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
          sx={{ backgroundColor: '#ffcc00', color: '#000' }}>
          Ontvangst bevestigen
        </Button>
      </Box>
    </Box>
  );
}

// ─── Tab 2: Voorraadwaarde ────────────────────────────────────────────────────
function StockValueTab({ products }) {
  const { data, loading, refetch } = useApi(`${API}/stock-value`);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filterNoPriceOnly, setFilterNoPriceOnly] = useState(false);

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);

    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    const header = lines[0].split(/[,;]/);
    const skuIdx = header.findIndex(h => h.trim().toLowerCase() === 'sku');
    const priceIdx = header.findIndex(h => h.trim().toLowerCase().includes('prijs') || h.trim().toLowerCase().includes('price'));

    if (skuIdx === -1 || priceIdx === -1) {
      setImportResult({ ok: false, msg: 'CSV moet kolommen "sku" en "prijs" (of "price") bevatten' });
      setImporting(false);
      return;
    }

    const prices = lines.slice(1).map(l => {
      const cols = l.split(/[,;]/);
      return { sku: cols[skuIdx]?.trim(), avg_purchase_price: parseFloat(cols[priceIdx]?.replace(',', '.')) };
    }).filter(p => p.sku && !isNaN(p.avg_purchase_price));

    const res = await fetch(`${API}/import-prices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': APP_API_KEY },
      body: JSON.stringify({ prices })
    });
    const result = await res.json();
    const ok = result.results?.filter(r => r.ok).length || 0;
    const fail = result.results?.filter(r => !r.ok).length || 0;
    setImportResult({ ok: true, msg: `${ok} prijzen bijgewerkt${fail > 0 ? `, ${fail} mislukt` : ''}` });
    setImporting(false);
    refetch();
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const rows = (data?.rows || []).filter(r => !filterNoPriceOnly || !r.has_price);
  const grandTotal = data?.grandTotal || 0;

  // Waarde per magazijn aggregeren
  const whTotals = {};
  (data?.rows || []).forEach(r => {
    r.warehouses.forEach(w => {
      whTotals[w.warehouse] = (whTotals[w.warehouse] || 0) + w.value;
    });
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Voorraadwaarde</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant={filterNoPriceOnly ? 'contained' : 'outlined'}
            onClick={() => setFilterNoPriceOnly(v => !v)}
            sx={filterNoPriceOnly ? { backgroundColor: '#ffcc00', color: '#000' } : {}}>
            Zonder prijs
          </Button>
          <Button component="label" size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={importing}>
            {importing ? <CircularProgress size={16} /> : 'CSV import'}
            <input type="file" accept=".csv" hidden onChange={handleCsvImport} />
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={refetch} size="small">Vernieuwen</Button>
        </Box>
      </Box>

      {importResult && (
        <Alert severity={importResult.ok ? 'success' : 'error'} sx={{ mb: 2 }}
          onClose={() => setImportResult(null)}>{importResult.msg}</Alert>
      )}

      {/* Totaalkaartjes */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary">Totale voorraadwaarde</Typography>
          <Typography variant="h5" fontWeight="bold" color="primary">€ {grandTotal.toFixed(2)}</Typography>
        </Paper>
        {Object.entries(whTotals).map(([wh, val]) => (
          <Paper key={wh} elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, minWidth: 130 }}>
            <Typography variant="caption" color="text.secondary">{wh}</Typography>
            <Typography variant="h6" fontWeight="bold">€ {val.toFixed(2)}</Typography>
          </Paper>
        ))}
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #fff8e1', backgroundColor: '#fffde7', borderRadius: 2, minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary">Zonder bekende prijs</Typography>
          <Typography variant="h6" fontWeight="bold" color="warning.main">
            {(data?.rows || []).filter(r => !r.has_price && r.total_qty > 0).length} SKU's
          </Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Product</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gem. inkoopprijs</TableCell>
              {Object.keys(whTotals).map(wh => (
                <TableCell key={wh} align="center" sx={{ fontWeight: 'bold' }}>{wh}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>Totaal stuks</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9' }}>Totale waarde</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Prijsbron</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.sku} hover sx={!r.has_price ? { backgroundColor: '#fffde7' } : {}}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.sku}</Typography>
                </TableCell>
                <TableCell align="right">
                  {r.has_price
                    ? <Typography variant="body2">€ {r.avg_purchase_price.toFixed(4)}</Typography>
                    : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
                {Object.keys(whTotals).map(wh => {
                  const whData = r.warehouses.find(w => w.warehouse === wh);
                  return (
                    <TableCell key={wh} align="center">
                      <Typography variant="body2">{whData?.quantity ?? 0}</Typography>
                      {r.has_price && whData?.quantity > 0 && (
                        <Typography variant="caption" color="text.secondary">€ {whData.value.toFixed(2)}</Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="center" sx={{ backgroundColor: '#f1f8f1' }}>
                  <Typography variant="body2" fontWeight="bold">{r.total_qty}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#f1f8f1' }}>
                  {r.has_price
                    ? <Typography variant="body2" fontWeight="bold">€ {r.total_value.toFixed(2)}</Typography>
                    : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={r.price_note}>
                      <InfoIcon sx={{ fontSize: 14, color: r.has_price ? 'text.secondary' : 'warning.main' }} />
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">{r.price_note}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
        CSV formaat voor prijsimport: <strong>sku;prijs</strong> (scheidingsteken komma of puntkomma). 
        Voorbeeld: <code>HHZB1LF50;8.50</code>. Na import wordt de gemiddelde prijs bijgewerkt.
      </Alert>
    </Box>
  );
}

// ─── Hoofd component ──────────────────────────────────────────────────────────
export default function Purchase() {
  const [tab, setTab] = useState(0);
  const { data: products, loading: prodLoading, refetch: refetchProducts } = useApi(`${INV_API}/products`);
  const [migrated, setMigrated] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const runMigration = async () => {
    setMigrating(true);
    await fetch(`${API}/migrate`, { method: 'POST' });
    setMigrated(true);
    setMigrating(false);
    window.location.reload();
  };

  // Probeer te detecteren of tabellen bestaan
  const [tablesExist, setTablesExist] = useState(null);
  useEffect(() => {
    fetch(`${API}/suppliers`).then(r => {
      setTablesExist(r.ok || r.status !== 500);
    }).catch(() => setTablesExist(false));
  }, [migrated]);

  if (tablesExist === false && !migrated) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Inkoopmodule initialiseren</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          De database tabellen voor de inkoopmodule moeten eenmalig worden aangemaakt.
        </Typography>
        <Button onClick={runMigration} disabled={migrating} variant="contained"
          sx={{ backgroundColor: '#ffcc00', color: '#000' }}>
          {migrating ? <CircularProgress size={20} /> : 'Database initialiseren'}
        </Button>
      </Box>
    );
  }

  if (tablesExist === null) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab icon={<SupplierIcon />} iconPosition="start" label="Leveranciers" />
        <Tab icon={<LinkIcon />} iconPosition="start" label="SKU Koppeling" />
        <Tab icon={<OrderIcon />} iconPosition="start" label="Inkooporders" />
        <Tab icon={<StockValueIcon />} iconPosition="start" label="Voorraadwaarde" />
        <Tab icon={<ForecastIcon />} iconPosition="start" label="Prognose" />
      </Tabs>

      {tab === 0 && <SuppliersTab />}
      {tab === 1 && <SkuLinkTab />}
      {tab === 2 && <OrdersTab products={prodLoading ? [] : (products || [])} refetchProducts={refetchProducts} />}
      {tab === 3 && <StockValueTab products={products} />}
      {tab === 4 && <Prognose />}
    </Box>
  );
}
